import React from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import Icon from '../../components/Icon';

// Working designs gallery — each tile links to the live feature page.
// Layout mirrors the prototype's five-section structure (26 surfaces).
interface TileSpec {
  href: string;
  external?: boolean;           // true = window.location / open
  title: string;
  titleEm?: string;
  desc: string;
  icon: string;
  tone: 'default' | 'terra' | 'sage' | 'warn' | 'forest' | 'sky' | 'lavender' | 'rose';
  badge?: string;
  hero?: boolean;               // spans two columns
}

const EVERYDAY: TileSpec[] = [
  { href: '/app/home', title: 'Dashboard', titleEm: '& calendar.', desc: "Today at a glance — shared calendar, chores for the house, what's for dinner, who's home. The front door.", icon: 'home', tone: 'terra', badge: 'HUB', hero: true },
  { href: '/app/chat', title: 'Chat.', desc: 'Threaded, pinnable, photo-friendly. Everyday talk and the good stuff, one place.', icon: 'chat_bubble', tone: 'sage' },
];

const HOME_TILES: TileSpec[] = [
  { href: '/app/lists', title: 'Shopping.', desc: 'Shared lists. Pantry auto-replenishes. Aisle-ordered in the shop.', icon: 'shopping_cart', tone: 'warn' },
  { href: '/app/kitchen', title: 'Kitchen.', desc: 'Meal planner, recipes, dinner tonight. Pulls from pantry & diaries.', icon: 'restaurant', tone: 'forest' },
  { href: '/app/pantry', title: 'Pantry.', desc: 'Your kitchen, catalogued. Expiries, low-stock, zero-waste nudges.', icon: 'inventory_2', tone: 'sky' },
  { href: '/app/tasks', title: 'Tasks', titleEm: '& chores.', desc: 'Point-weighted fair-share. Invisible workload, visible.', icon: 'check_circle', tone: 'sage' },
  { href: '/app/reminders', title: 'Reminders.', desc: 'MOT, council tax, TV licence. Escalating nudges.', icon: 'notifications', tone: 'terra' },
  { href: '/app/budget', title: 'Budget.', desc: 'Shared pots, forecast, kid-safe view. Calm money.', icon: 'account_balance_wallet', tone: 'lavender' },
];

const FAMILY_TILES: TileSpec[] = [
  { href: '/app/school-calendar', title: 'School.', desc: 'Term dates, uniform, trips, reading diary. Per-child.', icon: 'school', tone: 'sky' },
  { href: '/app/health', title: 'Health.', desc: 'NHS letters, jabs, scripts, appointments, growth charts.', icon: 'local_hospital', tone: 'rose' },
  { href: '/app/health-coach', title: 'Health', titleEm: 'coach.', desc: 'One AI coach for the whole house — meals, workouts, hydration, weekly check-ins.', icon: 'nutrition', tone: 'forest', badge: 'NEW' },
  { href: '/app/contacts', title: 'Contacts.', desc: 'Your village: plumber, nursery, GP, Nan.', icon: 'contacts', tone: 'sage' },
  { href: '/app/documents', title: 'Documents.', desc: "Passports, insurance, tenancy. OCR'd, expiry-alarmed.", icon: 'folder_open', tone: 'warn' },
  { href: '/app/special-days', title: 'Special', titleEm: 'days.', desc: 'Birthdays, anniversaries, Eid. Gift budgets & card lead times.', icon: 'star', tone: 'warn' },
  { href: '/app/locator', title: 'Family map.', desc: 'Places, not surveillance. Arrived-safely pings, consent-first.', icon: 'location_on', tone: 'sky' },
];

const MEMORIES: TileSpec[] = [
  { href: '/app/notes', title: 'Notes.', desc: 'Fridge notes, sitter briefs, packing lists.', icon: 'sticky_note_2', tone: 'warn' },
  { href: '/app/wishlist', title: 'Wishlist.', desc: 'Gift lists, birthdays, Christmas. No more duplicate presents.', icon: 'redeem', tone: 'lavender' },
  { href: '/app/polls', title: 'Polls.', desc: 'Pizza or pasta? Takes the argument out of it.', icon: 'bar_chart', tone: 'sage' },
];

const META: { href: string; external?: boolean; title: string; desc: string; icon: string }[] = [
  { href: '/', external: true, title: 'Landing.', desc: "Marketing site. For people who haven't met us yet.", icon: 'home' },
  { href: '/register', external: true, title: 'Onboarding.', desc: 'Welcome, sign-in, household setup. First 90 seconds.', icon: 'person_add' },
  { href: '/app/profile', title: 'Profile.', desc: 'Your card in the house. Pronouns, pref, perms.', icon: 'person' },
  { href: '/app/more', title: 'Settings.', desc: 'Theme, tweak home, notifications, privacy.', icon: 'settings' },
  { href: '/app/premium', title: 'Premium.', desc: 'Upgrade paths. Family plan, yearly saves 30%.', icon: 'workspace_premium' },
  { href: '/app/terms', title: 'Legal.', desc: 'Privacy, terms, data export. Plain English.', icon: 'gavel' },
  { href: '/app/__not-a-real-route__', title: '404.', desc: 'The kindest dead-end page on the internet.', icon: 'report' },
];

const FeaturesPage: React.FC = () => {
  const history = useHistory();

  const go = (href: string, external?: boolean) => {
    if (external) window.location.href = href;
    else history.push(href);
  };

  const renderTile = (t: TileSpec) => (
    <button
      key={t.href + t.title}
      onClick={() => go(t.href, t.external)}
      className={`fhv2-feature-tile tone-${t.tone} ${t.hero ? 'is-hero' : ''} fhv2-unset`}
    >
      {t.badge && <span className="fhv2-feature-badge">{t.badge}</span>}
      <div className="fhv2-feature-ic">
        <Icon name={t.icon} size={20} color="currentColor" weight="duotone" />
      </div>
      <h3>
        {t.title}
        {t.titleEm ? <> <em>{t.titleEm}</em></> : null}
      </h3>
      <p>{t.desc}</p>
      <span className="fhv2-feature-arrow">Open →</span>
    </button>
  );

  return (
    <IonPage>
      <IonContent>
        <div className="fhv2-features-page">
          {/* Hero */}
          <div className="fhv2-features-hero">
            <div className="fhv2-features-eyebrow">Famhearth · 26 surfaces · 1 household OS</div>
            <h1>A home, <em>with a brain.</em></h1>
            <p>Every surface below is a working design — desktop + mobile, hi-fi, connected. Click any tile to open it.</p>
            <div className="fhv2-kpi-row">
              <div className="fhv2-kpi"><div className="n">26</div><div className="l">Surfaces</div></div>
              <div className="fhv2-kpi"><div className="n">52</div><div className="l">Views (desktop + mobile)</div></div>
              <div className="fhv2-kpi"><div className="n">5</div><div className="l">Family members</div></div>
              <div className="fhv2-kpi"><div className="n">1</div><div className="l">Design system</div></div>
            </div>
          </div>

          {/* 01 · Every day */}
          <section className="fhv2-features-section">
            <div className="fhv2-features-eyebrow2">01 · Every day · where the day lives</div>
            <h2>Every day, the shared surfaces.</h2>
            <p className="fhv2-features-lead">The things you touch every morning — what's happening, who needs what, what's in the fridge.</p>
            <div className="fhv2-feature-grid">
              {EVERYDAY.map(renderTile)}
            </div>
          </section>

          <div className="fhv2-features-divider" />

          {/* 02 · Home */}
          <section className="fhv2-features-section">
            <div className="fhv2-features-eyebrow2">02 · Home · food, chores, admin</div>
            <h2>The house, running itself.</h2>
            <p className="fhv2-features-lead">Shopping, cooking, pantry, chores, renewals. The invisible work of a household.</p>
            <div className="fhv2-feature-grid">
              {HOME_TILES.map(renderTile)}
            </div>
          </section>

          <div className="fhv2-features-divider" />

          {/* 03 · Family */}
          <section className="fhv2-features-section">
            <div className="fhv2-features-eyebrow2">03 · Family · kids + kin</div>
            <h2>The people you love, held.</h2>
            <p className="fhv2-features-lead">School, health, documents, the village. Where this app earns its keep.</p>
            <div className="fhv2-feature-grid">
              {FAMILY_TILES.map(renderTile)}
            </div>
          </section>

          <div className="fhv2-features-divider" />

          {/* 04 · Memories */}
          <section className="fhv2-features-section">
            <div className="fhv2-features-eyebrow2">04 · Memories · the warm stuff</div>
            <h2>Keep the good things.</h2>
            <p className="fhv2-features-lead">Photos, notes, wishlists, family polls. Lightweight by design — these are meant to feel playful.</p>
            <div className="fhv2-feature-grid fhv2-feature-grid-4">
              {MEMORIES.map(renderTile)}
            </div>
          </section>

          <div className="fhv2-features-divider" />

          {/* 05 · Behind the scenes (meta row) */}
          <section className="fhv2-features-section">
            <div className="fhv2-features-eyebrow2">05 · Your account · sign-in, plan, settings</div>
            <h2>Behind the scenes.</h2>
            <p className="fhv2-features-lead">Onboarding, profile, plan, settings, and the unavoidable edges — 404, legal, landing.</p>
            <div className="fhv2-meta-row">
              {META.map(m => (
                <button
                  key={m.href + m.title}
                  onClick={() => go(m.href, m.external)}
                  className="fhv2-meta-tile fhv2-unset"
                >
                  <div className="fhv2-meta-ic">
                    <Icon name={m.icon} size={16} color="currentColor" weight="duotone" />
                  </div>
                  <h4>{m.title}</h4>
                  <p>{m.desc}</p>
                </button>
              ))}
            </div>
          </section>

          <footer className="fhv2-features-foot">
            <div>FAMHEARTH · WORKING DESIGNS · {new Date().getFullYear()}</div>
            <div>↑ click any tile to explore</div>
          </footer>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FeaturesPage;
